// frontend/src/App.js – NextScenes site shell + global navigation + footer (SESSION Login Portal)
// Updated: 2025-12-30-01 (AdminStories route wired)
// Updated: 2026-01-08 – Footer pages upgraded to editable content pages (FAQ/Blog/Contact/Terms/Privacy) + first-draft Terms/Privacy
// Updated: 2026-01-09 – FIX: stop legacy inline AdminUsers from shadowing the real AdminUsers page
// Updated: 2026-01-11 – Post-login routing + permission guards (no loops, deep-link safe)
// Updated: 2026-01-28 – Public profile route: /u/:username -> publicProfile page (guest-safe)
// Updated: 2026-01-30 – Group invite UX page: /invite?token=... (VerifyEmail-style)
// Updated: 2026-02-17 – ReaderView canonical URL (/reader-view) + storyId deep-link preservation
// Updated: 2026-02-22 – FIX: More dropdown closes on selection + Storylines menu opens All Forums/All Writing Modes (navResetToken)
// Updated: 2026-02-23 – ADD: My Shelf page routing + menu
// Updated: 2026-02-25 – ADD: First-login Welcome panel (Orientation modal, per-session per-account + “Don’t show again”)
// Updated: 2026-02-25 – FIX: Profile preference compatibility + Welcome preview hook
// Updated: 2026-02-25 – FIX: Welcome disable keys are now alias-aware (id/email/username) to match Profile v12
// Updated: 2026-02-25 – FIX: Welcome now shows on EVERY login until disabled (per-login latch; logout/login in same tab works)
// Updated: 2026-02-28 – ADD: App language toggle (EN/FR) for beta clarity + Register helper text sync
// Updated: 2026-04-20 – ADD: Story Dashboard route, URL handling, writer access guard, and More-menu entry
// Updated: 2026-04-26 – POLISH: Notifications unread badge in More menu + unread count helper
// Updated: 2026-04-26 – POLISH: Notification unread badge changed to NextScenes red
// Updated: 2026-05-29 – FOOTER: Align app footer links and copy with public site palace direction

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./App.css";

import VerifyEmail from "./pages/VerifyEmail";
import InviteAccept from "./pages/InviteAccept";

import Profile from "./pages/Profile";
import PublicProfile from "./pages/PublicProfile";

import Home from "./pages/Home";
import WriterStudio from "./WriterStudio";
import ReaderView from "./ReaderView";
import ChildrensBookReader from "./pages/ChildrensBookReader";
import ScreenplayReaderView from "./pages/ScreenplayReaderView";
import ScreenplayDiscovery from "./pages/ScreenplayDiscovery";
import ChildrensBookDiscovery from "./pages/ChildrensBookDiscovery";
import StoryHub from "./StoryHub";
import Forums from "./pages/Forums";
import Storylines from "./pages/Storylines";
import MyShelf from "./pages/MyShelf";
import Notifications from "./pages/Notifications";
import HowItWorks from "./pages/HowItWorks";
import Mystery250 from "./Mystery250";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Welcome from "./pages/Welcome";
import AdminDashboard from "./pages/AdminDashboard";
import AdminPanel from "./pages/AdminPanel";
import AdminStories from "./pages/AdminStories";
import AdminBetaFeedback from "./pages/AdminBetaFeedback";
import AdminContactInbox from "./pages/AdminContactInbox";
import AdminUsersPage from "./pages/AdminUsers";
import AdminWriterReviews from "./pages/AdminWriterReviews";
import AdminPublicHomepage from "./pages/AdminPublicHomepage";
import ContentStewardWorkspaceV1 from "./pages/ContentStewardWorkspaceV1";
import ResetPassword from "./pages/ResetPassword";
import logo from "./assets/nextscenes-logo.png";

import { useUser } from "./UserContext";
import { API_BASE, api } from "./api";
import BuildStamp from "./components/BuildStamp";
import NewsletterFooterPilotV1 from "./components/NewsletterFooterPilotV1";

import { TEXT } from "./i18n";

import StoryDashboard from "./pages/StoryDashboard";
import EditorialBoard from "./pages/EditorialBoard";
import ProjectHub from "./pages/ProjectHub";
import {
  projectHubNovelStoryPilotEnabled,
} from "./utils/projectHubPilot";

/* ------------------ constants ------------------ */

const POST_LOGIN_TARGET_KEY = "ns.postLoginTarget";
const PENDING_PROJECT_CATEGORY_KEY = "ns.nav.pendingProjectCategory";
const PUBLIC_SITE_URL =
  typeof window !== "undefined" && (
    window.location.hostname === "staging.nextscenes.org" ||
    window.location.hostname === "nextscenes-staging-frontend.onrender.com"
  )
    ? "https://nextscenes-public-staging.onrender.com"
    : (process.env.REACT_APP_PUBLIC_SITE_URL || "https://www.nextscenes.org");

// ✅ App language (beta clarity)
const APP_LANG_KEY = "ns.lang"; // "en" | "fr"

// ✅ Onboarding / Orientation (per-login per-account + optional permanent disable)
const WELCOME_NS = "ns.onboarding.welcome.v1";

// ✅ Back-compat key (old builds used it as “seen”)
// NOTE: In THIS build it is treated only as “disabled” when set to "1".
const WELCOME_SEEN_KEY = "ns.onboarding.welcome.v1.seen";

// ✅ Preview flag (sessionStorage) so Profile can force-open once
const WELCOME_PREVIEW_KEY = "ns.onboarding.welcome.v1.preview";

// ✅ NEW: per-login epoch (sessionStorage) so logout/login in same tab shows again
const WELCOME_LOGIN_EPOCH_PREFIX = `${WELCOME_NS}.loginEpoch.`;

/* ------------------ Language helpers ------------------ */

function getLangFromUrlOrStorage() {
  try {
    const qs = new URLSearchParams(window.location.search || "");
    const qLang = String(qs.get("lang") || "").trim().toLowerCase();
    if (qLang === "fr") return "fr";
    if (qLang === "en") return "en";
  } catch {}

  try {
    const s = String(window.localStorage.getItem(APP_LANG_KEY) || "").trim().toLowerCase();
    if (s === "fr" || s === "en") return s;
  } catch {}

  return "en";
}

function setAppLang(next) {
  const v = String(next || "").trim().toLowerCase() === "fr" ? "fr" : "en";
  try {
    window.localStorage.setItem(APP_LANG_KEY, v);
  } catch {}
  return v;
}

/* ------------------ TopNav ------------------ */

function TopNav({
  activePage,
  onGo,
  isAuthenticated,
  logout,
  canSeeWriterStudio,
  canSeeProjectHub,
  canAccessTextbookStudio,
  canSeeAdmin,
  canSeeEditorial,
  onOpenBetaFeedback,
  lang,
  onSetLang,
  notificationUnreadCount = 0,
  projectHubContext = {},
}) {
  function NavButton({ page, label }) {
    const isActive = activePage === page;
    return (
      <button
        type="button"
        className={"ns-topnav-link" + (isActive ? " ns-topnav-link-active" : "")}
        onClick={() => onGo(page)}
      >
        {label}
      </button>
    );
  }

  const t = TEXT[lang] || TEXT.en;
  const publicHomeHref = PUBLIC_SITE_URL.replace(/\/+$/, "") + (lang === "fr" ? "/fr" : "");

  const projectCategory =
    String(projectHubContext?.category || "").trim();

  const selectedProjectId =
    String(projectHubContext?.projectId || "").trim();

  const projectWorkspacePages = [
    "writerStudio",
    "storyDashboard",
    "editorialBoard",
  ];

  const inCreativeProjectContext =
    Boolean(
      canSeeProjectHub &&
      (
        activePage === "projectHub" ||
        projectCategory ||
        projectWorkspacePages.includes(activePage)
      )
    );

  const hasNovelStoryCategory =
    projectCategory === "story";

  const hasSelectedProject =
    Boolean(selectedProjectId);

  const isProjectHubLanding =
    Boolean(
      canSeeProjectHub &&
      activePage === "projectHub" &&
      !projectCategory
    );

  const safeNotificationUnreadCount = Math.max(0, Number(notificationUnreadCount || 0));
  const notificationBadgeLabel = safeNotificationUnreadCount > 99 ? "99+" : String(safeNotificationUnreadCount);

  function NotificationBadge({ small = false }) {
    if (safeNotificationUnreadCount <= 0) return null;

    return (
      <span
        aria-label={`${notificationBadgeLabel} unread notification${safeNotificationUnreadCount === 1 ? "" : "s"}`}
        title={`${notificationBadgeLabel} unread notification${safeNotificationUnreadCount === 1 ? "" : "s"}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: small ? 18 : 22,
          height: small ? 18 : 22,
          padding: small ? "0 5px" : "0 7px",
          borderRadius: 999,
          background: "#ef3b2d",
          color: "#ffffff",
          fontSize: small ? 10 : 11,
          fontWeight: 900,
          lineHeight: 1,
          boxShadow: "0 0 0 2px rgba(255,255,255,0.95), 0 2px 8px rgba(239,59,45,0.28)",
        }}
      >
        {notificationBadgeLabel}
      </span>
    );
  }

  // Explore dropdown:
  // - retain the full category registry and existing access flags;
  // - show only categories currently available to browse;
  // - keep suspended categories hidden here, not deleted or reactivated.
  function ExploreMenu() {
    const items = [
      {
        key: "story",
        page: "storylines",
        label:
          lang === "fr"
            ? "Romans & Histoires"
            : "Novels & Stories",
        available: true,
      },
      {
        key: "children",
        page: "childrensBookDiscovery",
        label:
          lang === "fr"
            ? "Livres pour enfants"
            : "Children’s Books",
        available: true,
      },
      {
        key: "essay",
        label:
          lang === "fr"
            ? "Essais & Recueils d’essais"
            : "Essays & Essay Collections",
        available: false,
        disabled: true,
      },
      {
        key: "textbook",
        page: "projectHub",
        options: {
          projectCategory: "textbook",
          source: "explore_menu_textbook_v1",
        },
        label:
          lang === "fr"
            ? "Manuels scolaires"
            : "Textbooks",
        available: true,
      },
      {
        key: "research",
        label:
          lang === "fr"
            ? "Recherche"
            : "Research",
        available: false,
        disabled: true,
      },
      {
        key: "technical",
        label:
          lang === "fr"
            ? "Manuels techniques"
            : "Technical Manuals",
        available: false,
        disabled: true,
      },
      {
        key: "screenplay",
        page: "screenplayDiscovery",
        label:
          lang === "fr"
            ? "Scénarios"
            : "Screenplays",
        available: true,
      },
      {
        key: "stage",
        label:
          lang === "fr"
            ? "Pièces de théâtre"
            : "Stage Plays",
        available: false,
        disabled: true,
      },
      {
        key: "music",
        label:
          lang === "fr"
            ? "Musique & Paroles"
            : "Music & Lyrics",
        available: false,
        disabled: true,
      },
    ];

    const isActive =
      activePage === "storylines" ||
      activePage === "childrensBookDiscovery" ||
      activePage === "screenplayDiscovery" ||
      (
        activePage === "projectHub" &&
        ["screenplay", "textbook"].includes(projectCategory)
      );

    const detailsRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [textbookInfoOpen, setTextbookInfoOpen] = useState(false);

    function closeMenu() {
      setOpen(false);
      try {
        if (detailsRef.current) {
          detailsRef.current.removeAttribute("open");
        }
      } catch {}
    }

    useEffect(() => {
      closeMenu();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePage, projectCategory]);

    useEffect(() => {
      if (!open) return;

      function onDocDown(e) {
        try {
          const el = detailsRef.current;
          if (!el) return;
          if (el.contains(e.target)) return;
          closeMenu();
        } catch {}
      }

      document.addEventListener("mousedown", onDocDown);
      return () =>
        document.removeEventListener("mousedown", onDocDown);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    function openExploreItem(it) {
      if (it.disabled) return;

      closeMenu();

      // Public discovery of the category is not public access to books.
      if (it.key === "textbook") {
        if (canAccessTextbookStudio) {
          onGo("projectHub", it.options);
        } else {
          setTextbookInfoOpen(true);
        }
        return;
      }

      if (it.page === "storylines") {
        onGo("storylines", {
          resetStorylines: true,
          source: "explore_menu",
        });
        return;
      }


      onGo(it.page, it.options);
    }

    return (
      <>
      <details
        ref={detailsRef}
        className={
          "ns-more ns-explore" +
          (isActive ? " ns-more-active" : "")
        }
        open={open}
        onToggle={(e) => {
          try {
            setOpen(Boolean(e?.target?.open));
          } catch {}
        }}
      >
        <summary
          className={
            "ns-topnav-link" +
            ((open || isActive)
              ? " ns-topnav-link-active"
              : "")
          }
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
          aria-label={
            lang === "fr"
              ? "Explorer NextScenes"
              : "Explore NextScenes"
          }
        >
          <span>
            {lang === "fr" ? "Explorer" : "Explore"}
          </span>
          <span
            aria-hidden="true"
            style={{
              fontSize: 10,
              lineHeight: 1,
              opacity: 0.82,
            }}
          >
            ▾
          </span>
        </summary>

        <div
          className="ns-more-menu ns-explore-menu"
          role="menu"
          aria-label={
            lang === "fr"
              ? "Explorer"
              : "Explore"
          }
        >
          {items.filter((it) => it.available && !it.disabled).map((it) => (
            <button
              key={it.key}
              type="button"
              className={
                "ns-more-item ns-explore-item" +
                (it.disabled ? " is-disabled" : "")
              }
              disabled={Boolean(it.disabled)}
              aria-disabled={Boolean(it.disabled)}
              title={
                it.disabled
                  ? (
                      lang === "fr"
                        ? "Prévu pour une phase ultérieure"
                        : "Planned for a later phase"
                    )
                  : undefined
              }
              onClick={() => openExploreItem(it)}
            >
              <span>{it.label}</span>

              <span
                className={
                  "ns-explore-status" +
                  (it.available ? " is-available" : "")
                }
              >
                {it.key === "textbook"
                  ? (lang === "fr" ? "Accès professionnel" : "Professional access")
                  : it.available
                  ? (
                      lang === "fr"
                        ? "Disponible"
                        : "Available"
                    )
                  : (
                      lang === "fr"
                        ? "À venir"
                        : "Coming later"
                    )}
              </span>
            </button>
          ))}
        </div>
      </details>
      {textbookInfoOpen ? (
        <div
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setTextbookInfoOpen(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2250,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(2, 6, 23, 0.55)",
            padding: 16,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="ns-textbook-professional-title"
            className="ns-card"
            style={{
              width: "min(460px, 100%)",
              padding: 22,
              borderRadius: 16,
              background: "#fff",
              boxShadow: "0 18px 48px rgba(0,0,0,0.25)",
            }}
          >
            <h3 id="ns-textbook-professional-title" style={{ margin: "0 0 10px" }}>
              {lang === "fr" ? "Manuels scolaires · Studio professionnel" : "Textbooks · Professional Studio"}
            </h3>
            <p style={{ margin: "0 0 18px", lineHeight: 1.6, color: "#475569" }}>
              {lang === "fr"
                ? "NextScenes Textbook V1 est un espace professionnel de rédaction et de préparation à la publication. Ce n’est pas une bibliothèque publique ni une rubrique Reader View. L’accès est réservé aux auteurs autorisés."
                : "NextScenes Textbook V1 is a professional writing and publication workspace. It is not a public textbook library or part of Reader View. Access is reserved for authorized writers."}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button type="button" className="ns-button ns-button-outline" onClick={() => setTextbookInfoOpen(false)}>
                {lang === "fr" ? "Fermer" : "Close"}
              </button>
              {!isAuthenticated ? (
                <button
                  type="button"
                  className="ns-button ns-button-primary"
                  onClick={() => { setTextbookInfoOpen(false); onGo("login"); }}
                >
                  {lang === "fr" ? "Se connecter" : "Sign in"}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      </>
    );
  }

  // More dropdown:
  // - deliberately small inside the Project Hub journey;
  // - existing broader menu remains available elsewhere.
  function MoreMenu() {
    const items = [];

    if (isProjectHubLanding) {
      if (isAuthenticated) {
        items.push({
          page: "profile",
          label:
            lang === "fr"
              ? "Mon profil"
              : "My Profile",
        });

        items.push({
          page: "myShelf",
          label:
            lang === "fr"
              ? "Mon étagère"
              : "My Shelf",
        });

        items.push({
          page: "projectHub",
          label:
            lang === "fr"
              ? "Mes projets"
              : "My Projects",
          options: {
            projectCategory: "story",
            source: "project_hub_compact_menu",
          },
        });

        items.push({
          page: "notifications",
          label:
            t.notifications ||
            "Notifications",
          badge:
            safeNotificationUnreadCount > 0
              ? notificationBadgeLabel
              : "",
        });
      }
    } else {
      items.push({
        page: "forums",
        label: t.forums,
      });

      items.push({
        page: "storylines",
        label: t.storylines,
      });

      if (isAuthenticated) {
        items.push({
          page: "myShelf",
          label: t.myShelf,
        });

        items.push({
          page: "notifications",
          label:
            t.notifications ||
            "Notifications",
          badge:
            safeNotificationUnreadCount > 0
              ? notificationBadgeLabel
              : "",
        });

        items.push({
          action: "betaFeedback",
          label: t.betaFeedback,
        });

        items.push({
          page: "profile",
          label: t.profile,
        });
      }

      if (canSeeProjectHub) {
        items.push({
          page: "projectHub",
          label:
            lang === "fr"
              ? "Hub de projets"
              : "Project Hub",
        });
      }

      if (canSeeWriterStudio) {
        items.push({
          page: "storyDashboard",
          label:
            t.storyDashboard ||
            "Story Dashboard",
        });

        items.push({
          page: "editorialBoard",
          label:
            t.editorialBoard ||
            "Editorial Board",
        });
      }

      items.push({
        page: "mystery250",
        label: t.mystery250,
      });

      if (canSeeEditorial) {
        items.push({
          page: "contentSteward",
          label: lang === "fr" ? "Responsable du contenu" : "Content Steward",
        });
      }

      if (canSeeAdmin) {
        items.push({
          page: "admin",
          label:
            lang === "fr"
              ? "Console d’administration"
              : "Admin Console",
        });

        items.push({
          page: "adminPublicHomepage",
          label:
            t.adminPublicHomepage ||
            "Public Homepage",
        });

        items.push({
          page: "adminWriterReviews",
          label:
            t.adminWriterReviews ||
            "Writer Reviews",
        });

      }
    }

    const isActive =
      activePage === "forums" ||
      activePage === "storylines" ||
      activePage === "myShelf" ||
      activePage === "notifications" ||
      activePage === "profile" ||
      activePage === "projectHub" ||
      activePage === "storyDashboard" ||
      activePage === "editorialBoard" ||
      activePage === "adminPublicHomepage" ||
      activePage === "adminWriterReviews" ||
      activePage === "mystery250" ||
      activePage === "admin";

    if (!items.length) return null;

    const detailsRef = useRef(null);
    const [open, setOpen] = useState(false);

    function closeMenu() {
      setOpen(false);
      try {
        if (detailsRef.current) detailsRef.current.removeAttribute("open");
      } catch {}
    }

    useEffect(() => {
      closeMenu();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePage]);

    useEffect(() => {
      if (!open) return;

      function onDocDown(e) {
        try {
          const el = detailsRef.current;
          if (!el) return;
          if (el.contains(e.target)) return;
          closeMenu();
        } catch {}
      }

      document.addEventListener("mousedown", onDocDown);
      return () => document.removeEventListener("mousedown", onDocDown);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
      <details
        ref={detailsRef}
        className={"ns-more" + (isActive ? " ns-more-active" : "")}
        open={open}
        onToggle={(e) => {
          try {
            setOpen(Boolean(e?.target?.open));
          } catch {}
        }}
      >
        <summary className={"ns-topnav-link" + ((open || isActive) ? " ns-topnav-link-active" : "")} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <span>{t.more}</span>
          <NotificationBadge small />
        </summary>

        <div className="ns-more-menu">
          {items.map((it) => (
            <button
              key={it.page || it.action}
              type="button"
              className="ns-more-item"
              onClick={() => {
                closeMenu();

                if (it.action === "betaFeedback") {
                  if (typeof onOpenBetaFeedback === "function") onOpenBetaFeedback();
                  return;
                }

                // Storylines from menu must open as All Forums + All Writing Modes
                if (it.page === "storylines") {
                  onGo("storylines", { resetStorylines: true, source: "more_menu" });
                  return;
                }

                onGo(
                  it.page,
                  it.options
                );
              }}
              style={
                it.page === "notifications"
                  ? { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }
                  : undefined
              }
            >
              <span>{it.label}</span>
              {it.page === "notifications" && it.badge ? <NotificationBadge /> : null}
            </button>
          ))}
        </div>
      </details>
    );
  }

  // Language dropdown (future-proof: add more languages later)
  function LangMenu() {
    const items = [
      { code: "en", label: t.english },
      { code: "fr", label: t.french },
    ];

    const detailsRef = useRef(null);
    const [open, setOpen] = useState(false);

    function closeMenu() {
      setOpen(false);
      try {
        if (detailsRef.current) detailsRef.current.removeAttribute("open");
      } catch {}
    }

    useEffect(() => {
      closeMenu();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePage]);

    useEffect(() => {
      if (!open) return;

      function onDocDown(e) {
        try {
          const el = detailsRef.current;
          if (!el) return;
          if (el.contains(e.target)) return;
          closeMenu();
        } catch {}
      }

      document.addEventListener("mousedown", onDocDown);
      return () => document.removeEventListener("mousedown", onDocDown);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const currentLang = String(lang || "en").toLowerCase();
    const languageGatewayLabel = currentLang === "fr" ? "🇫🇷 Français" : "🇬🇧 English";

    return (
      <details
        ref={detailsRef}
        className="ns-lang"
        open={open}
        onToggle={(e) => {
          try {
            setOpen(Boolean(e?.target?.open));
          } catch {}
        }}
      >
        <summary
          className={"ns-topnav-link ns-lang-trigger" + (open ? " ns-topnav-link-active" : "")}
          aria-label={t.lang}
          title={t.lang}
        >
          {languageGatewayLabel}
        </summary>

        <div className="ns-lang-menu" role="menu" aria-label={t.lang}>
          {items.map((it) => {
            const isActive = String(lang || "en") === it.code;
            return (
              <button
                key={it.code}
                type="button"
                className={"ns-lang-item" + (isActive ? " is-active" : "")}
                onClick={() => {
                  closeMenu();
                  if (String(lang || "en") !== it.code) onSetLang(it.code);
                }}
                aria-current={isActive ? "true" : "false"}>
                {it.label}
              </button>
            );
          })}
        </div>
      </details>
    );
  }

  return (
    <div className="ns-topnav" style={{ position: "sticky", top: 0, zIndex: 999 }}>
      <div className="ns-topnav-inner">
        <button
  type="button"
  className="ns-logo"
  onClick={() => {
    if (!isAuthenticated) window.location.assign(publicHomeHref);
    else if (canSeeProjectHub && (canSeeWriterStudio || canAccessTextbookStudio)) onGo("projectHub");
    else onGo("storylines", { resetStorylines: true });
  }}
  title="NextScenes"
  aria-label={
    isAuthenticated && canSeeProjectHub
      ? "NextScenes Project Hub"
      : "NextScenes home"
  }
>
  <img src={logo} alt="NextScenes" className="ns-logo-image" />
</button>

        <div className="ns-topnav-links">
          {isAuthenticated && !canSeeProjectHub ? (
            <>
              <a href={publicHomeHref} className="ns-topnav-link">{t.home}</a>
              <NavButton page="storyHub" label={t.storyHub} />
              {canSeeWriterStudio ? (
                <NavButton page="writerStudio" label={t.writerStudio} />
              ) : null}
              <ExploreMenu />
              <NavButton page="readerView" label={t.readerView} />
              <MoreMenu />
            </>
          ) : !isAuthenticated ? (
            <>
              <a href={publicHomeHref} className="ns-topnav-link">{t.home}</a>
              <ExploreMenu />
              <NavButton page="readerView" label={t.readerView} />
            </>
          ) : (
            <>
              {inCreativeProjectContext ? (
                <>
                  <NavButton
                    page="projectHub"
                    label={
                      lang === "fr"
                        ? "Hub de projets"
                        : "Project Hub"
                    }
                  />

                  {hasNovelStoryCategory &&
                  hasSelectedProject ? (
                    <NavButton
                      page="writerStudio"
                      label={t.writerStudio}
                    />
                  ) : null}
                </>
              ) : (
                <>
                  <NavButton
                    page="projectHub"
                    label={
                      lang === "fr"
                        ? "Hub de projets"
                        : "Project Hub"
                    }
                  />
                </>
              )}

              <ExploreMenu />
              <NavButton
                page="readerView"
                label={t.readerView}
              />

              <MoreMenu />
            </>
          )}
        </div>

        <div className="ns-topnav-right">
          <LangMenu />

          <a
            className="ns-button ns-button-outline ns-topnav-action ns-topnav-mobile-hide ns-public-site-cta"
            href={publicHomeHref}
            style={{ textDecoration: "none" }}
          >
            {t.publicSite}
          </a>

          {isAuthenticated ? (
            <>
              <button
                type="button"
                className="ns-button ns-button-outline ns-topnav-action ns-topnav-mobile-hide"
                onClick={async () => {
                  try {
                    await Promise.resolve(logout());
                  } finally {
                    // The server session is cleared by logout(); move within the
                    // current app so Welcome back appears without a full reload.
                    onGo("login");
                  }
                }}
              >
                {t.logout}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="ns-button ns-button-outline ns-topnav-action ns-topnav-mobile-hide"
                onClick={() => onGo("login")}
              >
                {t.login}
              </button>

              <button
                type="button"
                className="ns-button ns-button-primary ns-topnav-action ns-topnav-mobile-hide"
                onClick={() => onGo("register")}
              >
                {t.createAccount}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------ Footer ------------------ */

function FooterBar({ onGo, lang = "en" }) {
  const isFR =
    String(lang || "en")
      .trim()
      .toLowerCase()
      .startsWith("fr");

  const footerText = isFR
    ? {
        explore: "Explorer",
        platform: "Plateforme",
        community: "Communauté",
        storylines: "Storylines",
        forWriters: "Pour les auteurs",
        foundingWriters: "Pilote des auteurs fondateurs",
        booksInDevelopment: "Livres en développement",
        publishedWorks: "Œuvres publiées",
        faq: "FAQ",
        mystery250: "Mystery250",
        terms: "Conditions d’utilisation",
        privacy: "Politique de confidentialité",
        writerRights: "Droits des auteurs",
        contributorPolicy: "Politique des contributeurs",
        illustrationPolicy: "Politique de téléversement des illustrations",
        benefitSharing: "Partage des bénéfices",
        plainTerms: "Conditions en langage simple",
        contact: "Contact",
        tagline: "Lire. Écrire. Imaginer. Grandir.",
        copy:
          "Des histoires en développement, des parcours publiés et une collaboration réfléchie pour les lecteurs, les auteurs, les écoles et les communautés créatives. L’inscription, la lecture et la participation essentielles sont gratuites.",
        navigationLabel: "Navigation du pied de page",
        homeLabel: "Accueil NextScenes",
        copyright:
          "Les histoires grandissent grâce au contrôle de l’auteur, à la curiosité des lecteurs et à une collaboration réfléchie.",
      }
    : {
        explore: "Explore",
        platform: "Platform",
        community: "Community",
        storylines: "Storylines",
        forWriters: "For Writers",
        foundingWriters: "Founding Writers Pilot",
        booksInDevelopment: "Books in development",
        publishedWorks: "Published works",
        faq: "FAQ",
        mystery250: "Mystery250",
        terms: "Terms of Service",
        privacy: "Privacy Policy",
        writerRights: "Writer Rights",
        contributorPolicy: "Contributor Policy",
        illustrationPolicy: "Illustration Upload Policy",
        benefitSharing: "Benefit Sharing",
        plainTerms: "Plain-Language Terms",
        contact: "Contact",
        tagline: "Read. Write. Imagine. Grow.",
        copy:
          "Stories in development, published journeys, and thoughtful collaboration for readers, writers, schools, and creative communities. Core registration, reading, and participation are free.",
        navigationLabel: "Footer navigation",
        homeLabel: "NextScenes home",
        copyright:
          "Stories grow with author control, reader curiosity, and thoughtful collaboration.",
      };

  const openExternal = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const footerColumns = [
    {
      title: footerText.explore,
      links: [
        {
          label: footerText.storylines,
          page: "storylines",
        },
        {
          label: footerText.forWriters,
          url: "https://nextscenes.org/for-writers",
        },
        {
          label: footerText.foundingWriters,
          url: isFR
            ? "https://nextscenes.org/fr/pilote-auteurs-fondateurs"
            : "https://nextscenes.org/founding-writers-pilot",
        },
        {
          label: footerText.booksInDevelopment,
          url: "https://nextscenes.org/books-in-progress",
        },
        {
          label: footerText.publishedWorks,
          url: "https://nextscenes.org",
        },
        {
          label: footerText.faq,
          page: "faq",
        },
        {
          label: footerText.mystery250,
          page: "mystery250",
        },
      ],
    },
    {
      title: footerText.platform,
      links: [
        {
          label: footerText.terms,
          page: "terms",
        },
        {
          label: footerText.privacy,
          page: "privacy",
        },
        {
          label: footerText.writerRights,
          url: isFR
            ? "https://nextscenes.org/fr/droits-des-auteurs"
            : "https://nextscenes.org/writer-rights",
        },
        {
          label: footerText.contributorPolicy,
          url: isFR
            ? "https://nextscenes.org/fr/politique-contributeurs"
            : "https://nextscenes.org/contributor-policy",
        },
        {
          label: footerText.illustrationPolicy,
          url: isFR
            ? "https://nextscenes.org/fr/politique-illustrations-televersements"
            : "https://nextscenes.org/illustration-upload-policy",
        },
        {
          label: footerText.benefitSharing,
          url: "https://nextscenes.org/benefit-sharing",
        },
        {
          label: footerText.plainTerms,
          url: isFR
            ? "https://nextscenes.org/fr/conditions-simples"
            : "https://nextscenes.org/plain-language-terms",
        },
        {
          label: footerText.contact,
          page: "contact",
        },
      ],
    },
    {
      title: footerText.community,
      links: [
        {
          label: "Facebook",
          url: "https://www.facebook.com/nextscenes",
        },
        {
          label: "YouTube",
          url: "https://www.youtube.com/@NextScenesOfficial",
        },
        {
          label: "Instagram",
          url: "https://www.instagram.com",
        },
        {
          label: "X",
          url: "https://x.com",
        },
      ],
    },
  ];

  const handleFooterLink = (item) => {
    if (item.page) {
      onGo(item.page);
      return;
    }

    if (item.url) openExternal(item.url);
  };

  return (
    <footer className="ns-footer ns-app-footer">
      <div className="ns-app-footer-inner">
        <section className="ns-app-footer-brand" aria-label="NextScenes">
          <button
            type="button"
            className="ns-app-footer-logoButton"
            onClick={() => onGo("home")}
            aria-label={footerText.homeLabel}
          >
            <img src={logo} alt="NextScenes" className="ns-app-footer-logo" />
          </button>

          <p className="ns-app-footer-tagline">{footerText.tagline}</p>
          <p className="ns-app-footer-copy">
            {footerText.copy}
          </p>
        </section>

        <nav className="ns-app-footer-columns" aria-label={footerText.navigationLabel}>
          {footerColumns.map((column) => (
            <section className="ns-app-footer-column" key={column.title}>
              <h2>{column.title}</h2>
              <div className="ns-app-footer-linkList">
                {column.links.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    className="ns-footer-link ns-app-footer-link"
                    onClick={() => handleFooterLink(item)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </nav>
      </div>

      {typeof window !== "undefined" && (
        window.location.hostname === "staging.nextscenes.org" ||
        window.location.hostname.includes("nextscenes-staging-frontend")
      ) ? <NewsletterFooterPilotV1 lang={lang} /> : null}
      <div className="ns-app-footer-bottom">
        © {new Date().getFullYear()} NextScenes.{" "}
        {footerText.copyright}
      </div>
    </footer>
  );
}

/* ------------------ Boot Screen ------------------ */

function BootScreen() {
  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 780, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0 }}>Loading…</h2>
        <p style={{ color: "#475569" }}>Restoring your session. Please wait.</p>
      </div>
    </div>
  );
}

/* ------------------ Access Denied ------------------ */

function AccessDenied({ onGo, title, message }) {
  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 820, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0 }}>{title || "Access denied"}</h2>
        <p style={{ color: "#475569", marginTop: 8 }}>
          {message || "You are logged in, but your account does not have permission to view this page."}
        </p>

        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn" type="button" onClick={() => (onGo ? onGo("home") : null)}>
            Go Home
          </button>
          <button className="btn" type="button" onClick={() => (onGo ? onGo("login") : null)}>
            Switch account
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------ Content Pages (Editable) ------------------ */

function LegalPage({ title, effectiveDate, intro, sections, onGo }) {
  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 920, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0 }}>{title}</h2>

        {effectiveDate ? (
          <div style={{ marginTop: 6, fontSize: 13, color: "#64748b" }}>
            <b>Effective Date:</b> {effectiveDate}
          </div>
        ) : null}

        {intro ? <p style={{ color: "#475569", marginTop: 12 }}>{intro}</p> : null}

        {Array.isArray(sections) && sections.length > 0 ? (
          <div style={{ marginTop: 14 }}>
            {sections.map((s, idx) => {
              return (
                <div key={idx} style={{ marginTop: idx === 0 ? 0 : 16 }}>
                  {s?.heading ? <h3 style={{ marginBottom: 8 }}>{s.heading}</h3> : null}

                  {Array.isArray(s?.paragraphs)
                    ? s.paragraphs.map((p, i) => (
                        <p
                          key={i}
                          style={{ color: "#0f172a", lineHeight: 1.6, marginTop: i === 0 ? 0 : 10 }}
                        >
                          {p}
                        </p>
                      ))
                    : null}

                  {Array.isArray(s?.bullets) && s.bullets.length > 0 ? (
                    <ul style={{ marginTop: 10, color: "#0f172a", lineHeight: 1.6 }}>
                      {s.bullets.map((b, j) => (
                        <li key={j} style={{ marginTop: j === 0 ? 0 : 6 }}>
                          {b}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn" type="button" onClick={() => (onGo ? onGo("home") : null)}>
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
}

function PolicyHubPage({ title, intro, href, onGo }) {
  const safeHref = String(href || "").trim() || "https://nextscenes.org";

  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 920, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0 }}>{title}</h2>

        <p style={{ color: "#475569", marginTop: 12 }}>
          {intro || "This document is maintained on the public site to keep one clear source of truth during beta."}
        </p>

        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <a
            className="btn"
            href={safeHref}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none", display: "inline-block" }}
          >
            Open on public site
          </a>

          <button className="btn" type="button" onClick={() => (onGo ? onGo("home") : null)}>
            Back to app
          </button>
        </div>

        <div style={{ marginTop: 12, fontSize: 12, color: "#64748b" }}>
          Tip: it opens in a new tab so you won’t lose your place in the app.
        </div>
      </div>
    </div>
  );
}

/* ------------------ Admin Users (LEGACY inline version) ------------------ */
/* IMPORTANT:
   This legacy component is kept ONLY to preserve file history/length.
   It is NOT used by routing anymore. The real page component is imported as AdminUsersPage.
*/

function AdminUsersLegacy({ onGo }) {
  const { user } = useUser();
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin";

  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [rows, setRows] = useState([]);

  function normRole(r) {
    const x = String(r || "").toLowerCase().trim();
    if (x === "power") return "writer";
    if (x === "writer" || x === "moderator" || x === "admin" || x === "user") return x;
    return "user";
  }

  function activeBtnStyle(isActive) {
    if (!isActive) return null;
    return { background: "#0f172a", color: "#fff", borderColor: "#0f172a", opacity: 1 };
  }

  function lockIfActive(isActive) {
    if (!isActive) return null;
    return { pointerEvents: "none", cursor: "default" };
  }

  async function load() {
    if (!isAdmin) return;
    setBusy(true);
    setErr("");
    setOk("");
    try {
      const qs = query.trim() ? `?query=${encodeURIComponent(query.trim())}&limit=80` : "?limit=80";
      const data = await api(`/api/admin/users/list${qs}`, { method: "GET" });
      setRows(Array.isArray(data?.users) ? data.users : []);
    } catch (e) {
      setErr(e?.message || "Failed to load users.");
      setRows([]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!isAdmin) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  async function setRole(email, newRole) {
    const cleanEmail = String(email || "").trim().toLowerCase();
    if (!cleanEmail) return;

    const roleReq = String(newRole || "").toLowerCase().trim();
    if (roleReq !== "user" && roleReq !== "writer" && roleReq !== "moderator" && roleReq !== "admin") return;

    setBusy(true);
    setErr("");
    setOk("");
    try {
      await api("/api/admin/users/role", {
        method: "PATCH",
        body: JSON.stringify({ email: cleanEmail, role: roleReq }),
      });
      setOk(`Role updated: ${cleanEmail} -> ${roleReq}`);
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to update role.");
    } finally {
      setBusy(false);
    }
  }

  async function setActive(email, isActive) {
    const cleanEmail = String(email || "").trim().toLowerCase();
    if (!cleanEmail) return;

    setBusy(true);
    setErr("");
    setOk("");
    try {
      await api("/api/admin/users/status", {
        method: "PATCH",
        body: JSON.stringify({ email: cleanEmail, isActive }),
      });
      setOk(`Status updated: ${cleanEmail} -> ${isActive ? "active" : "disabled"}`);
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to update status.");
    } finally {
      setBusy(false);
    }
  }

  async function setVerified(email, emailVerified) {
    const cleanEmail = String(email || "").trim().toLowerCase();
    if (!cleanEmail) return;

    setBusy(true);
    setErr("");
    setOk("");
    try {
      await api("/api/admin/users/verify", {
        method: "PATCH",
        body: JSON.stringify({ email: cleanEmail, emailVerified }),
      });
      setOk(`Verification updated: ${cleanEmail} -> ${emailVerified ? "verified" : "not verified"}`);
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to update verification.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteUser(email) {
    const cleanEmail = String(email || "").trim().toLowerCase();
    if (!cleanEmail) return;

    const yes = window.confirm(`Delete user ${cleanEmail}? This cannot be undone.`);
    if (!yes) return;

    setBusy(true);
    setErr("");
    setOk("");
    try {
      await api(`/api/admin/users?email=${encodeURIComponent(cleanEmail)}`, { method: "DELETE" });
      setOk(`Deleted: ${cleanEmail}`);
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to delete user.");
    } finally {
      setBusy(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 820, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0 }}>Admin Users</h2>
          <p style={{ color: "#475569" }}>This page is restricted to Admin accounts.</p>
          <button className="btn" type="button" onClick={() => onGo && onGo("home")}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ marginTop: 0 }}>Admin Users</h2>
            <p style={{ marginTop: 6, color: "#475569" }}>
              Manage roles, disable accounts, verify email, delete users.
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button className="btn" type="button" onClick={load} disabled={busy}>
              {busy ? "Working..." : "Refresh"}
            </button>
            <button className="btn" type="button" onClick={() => onGo && onGo("admin")}>
              Back to Dashboard
            </button>
          </div>
        </div>

        {err ? (
          <div className="banner" style={{ marginTop: 12 }}>
            {err}
          </div>
        ) : null}
        {ok ? (
          <div className="banner" style={{ marginTop: 12 }}>
            {ok}
          </div>
        ) : null}

        <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search email, username, name…"
            style={{ padding: 10, borderRadius: 10, border: "1px solid #e5e7eb", minWidth: 280 }}
          />
          <button className="btn" type="button" onClick={load} disabled={busy}>
            Search
          </button>

          <button className="btn" type="button" onClick={() => onGo && onGo("adminPanel")}>
            Writer Requests
          </button>
          <button className="btn" type="button" onClick={() => onGo && onGo("adminStories")}>
            Storylines (Admin)
          </button>
        </div>

        <div style={{ marginTop: 14, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>
                <th style={{ padding: 10 }}>Email</th>
                <th style={{ padding: 10 }}>Username</th>
                <th style={{ padding: 10 }}>Name</th>
                <th style={{ padding: 10 }}>Role</th>
                <th style={{ padding: 10 }}>Status</th>
                <th style={{ padding: 10 }}>Verified</th>
                <th style={{ padding: 10 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((u) => {
                const email = String(u?.email || "").toLowerCase();
                const username = u?.username || "";
                const name = u?.name || "";
                const role2 = normRole(u?.role);
                const isActiveNow = u?.isActive !== false;
                const isVerifiedNow = u?.emailVerified === true;

                const isRoleUser = role2 === "user";
                const isRoleWriter = role2 === "writer";
                const isRoleModerator = role2 === "moderator";
                const isRoleAdmin = role2 === "admin";

                return (
                  <tr key={u.id || email} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: 10, fontWeight: 800 }}>{email}</td>
                    <td style={{ padding: 10 }}>{username}</td>
                    <td style={{ padding: 10 }}>{name}</td>
                    <td style={{ padding: 10 }}>{role2}</td>
                    <td style={{ padding: 10 }}>{isActiveNow ? "Active" : "Disabled"}</td>
                    <td style={{ padding: 10 }}>{isVerifiedNow ? "Yes" : "No"}</td>

                    <td style={{ padding: 10 }}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          className="btn"
                          type="button"
                          disabled={busy}
                          aria-disabled={isRoleUser ? "true" : "false"}
                          style={{ ...(activeBtnStyle(isRoleUser) || {}), ...(lockIfActive(isRoleUser) || {}) }}
                          onClick={() => {
                            if (busy || isRoleUser) return;
                            setRole(email, "user");
                          }}
                          title={isRoleUser ? "Current role" : "Set role to user"}
                        >
                          Role: user
                        </button>

                        <button
                          className="btn"
                          type="button"
                          disabled={busy}
                          aria-disabled={isRoleWriter ? "true" : "false"}
                          style={{ ...(activeBtnStyle(isRoleWriter) || {}), ...(lockIfActive(isRoleWriter) || {}) }}
                          onClick={() => {
                            if (busy || isRoleWriter) return;
                            setRole(email, "writer");
                          }}
                          title={isRoleWriter ? "Current role" : "Set role to writer"}
                        >
                          Role: writer
                        </button>

                        <button
                          className="btn"
                          type="button"
                          disabled={busy}
                          aria-disabled={isRoleModerator ? "true" : "false"}
                          style={{ ...(activeBtnStyle(isRoleModerator) || {}), ...(lockIfActive(isRoleModerator) || {}) }}
                          onClick={() => {
                            if (busy || isRoleModerator) return;
                            setRole(email, "moderator");
                          }}
                          title={isRoleModerator ? "Current role" : "Set role to moderator"}
                        >
                          Role: moderator
                        </button>

                        <button
                          className="btn"
                          type="button"
                          disabled={busy}
                          aria-disabled={isRoleAdmin ? "true" : "false"}
                          style={{ ...(activeBtnStyle(isRoleAdmin) || {}), ...(lockIfActive(isRoleAdmin) || {}) }}
                          onClick={() => {
                            if (busy || isRoleAdmin) return;
                            setRole(email, "admin");
                          }}
                          title={isRoleAdmin ? "Current role" : "Set role to admin"}
                        >
                          Role: admin
                        </button>

                        <button
                          className="btn"
                          type="button"
                          disabled={busy}
                          aria-disabled="false"
                          style={activeBtnStyle(!isActiveNow) || null}
                          onClick={() => {
                            if (busy) return;
                            setActive(email, !isActiveNow);
                          }}
                          title={isActiveNow ? "Disable this user" : "Enable this user"}
                        >
                          {isActiveNow ? "Disable" : "Enable"}
                        </button>

                        <button
                          className="btn"
                          type="button"
                          disabled={busy}
                          aria-disabled={isVerifiedNow ? "true" : "false"}
                          style={activeBtnStyle(isVerifiedNow) || null}
                          onClick={() => {
                            if (busy) return;
                            setVerified(email, !isVerifiedNow);
                          }}
                          title={isVerifiedNow ? "Mark as not verified" : "Mark as verified"}
                        >
                          {isVerifiedNow ? "Unverify" : "Verify"}
                        </button>

                        <button className="btn" type="button" disabled={busy} onClick={() => deleteUser(email)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 12, color: "#64748b" }}>
                    No users found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
          Note: you cannot disable/delete yourself or demote the last active admin.
        </div>
      </div>
    </div>
  );
}


/* ------------------ AppShell helpers ------------------ */

function clearGuestAuthArtifacts() {
  try {
    const keys = [
      "ns.auth",
      "ns.user",
      "ns.username",
      "ns.role",
      "ns.openWriterStudio",
      "ns.writerStudio.focusStoryId",
      "ns.writerStudio.storyId",
      "ns.threadTargetStoryId",
      "ns.threadTargetCommentId",
      "ns.reader.focusStoryId",
      "ns.reader.focusCommentId",
    ];
    keys.forEach((k) => window.localStorage.removeItem(k));
    const path = String(window.location.pathname || "").toLowerCase();
    if (!["/login", "/register", "/verify-email"].includes(path)) {
      window.localStorage.removeItem(POST_LOGIN_TARGET_KEY);
      window.localStorage.removeItem(PENDING_PROJECT_CATEGORY_KEY);
    }
  } catch {}
}

function setPostLoginTarget(page, category = "") {
  try {
    const p = String(page || "").trim();
    if (!p) return;
    window.localStorage.setItem(POST_LOGIN_TARGET_KEY, p);
    if (p === "projectHub") {
      const candidate = String(category || "").trim().toLowerCase();
      if (["story", "children", "textbook", "screenplay"].includes(candidate)) {
        window.localStorage.setItem(PENDING_PROJECT_CATEGORY_KEY, candidate);
      } else {
        window.localStorage.removeItem(PENDING_PROJECT_CATEGORY_KEY);
      }
    }
  } catch {}
}

function parsePublicUsernameFromPathname(pathname) {
  try {
    const raw = String(pathname || "/");
    const lower = raw.toLowerCase();
    if (!lower.startsWith("/u/")) return "";
    const parts = lower.split("/").filter(Boolean);
    if (parts.length < 2) return "";
    const uname = String(parts[1] || "").trim();
    return uname;
  } catch {
    return "";
  }
}

function parseInviteTokenFromLocation() {
  try {
    const qs = new URLSearchParams(window.location.search || "");
    return String(qs.get("token") || "").trim();
  } catch {
    return "";
  }
}

// ✅ Welcome keying (alias-aware to match Profile v12)
function welcomeIdentsFromUser(user) {
  try {
    const out = [];

    const id = String(user?.id || user?._id || "").trim();
    if (id) out.push(id);

    const email = String(user?.email || "").trim().toLowerCase();
    if (email) out.push(email);

    const username = String(user?.username || "").trim().toLowerCase();
    if (username) out.push(username);

    const uniq = [];
    const seen = new Set();
    for (const v of out) {
      const k = String(v || "");
      if (!k) continue;
      if (seen.has(k)) continue;
      seen.add(k);
      uniq.push(k);
    }
    return uniq.length ? uniq : ["unknown"];
  } catch {
    return ["unknown"];
  }
}

function welcomePrimaryIdentFromUser(user) {
  try {
    const list = welcomeIdentsFromUser(user);
    return list[0] || "unknown";
  } catch {
    return "unknown";
  }
}

function welcomeDisableKeysForUser(user) {
  const idents = welcomeIdentsFromUser(user);
  return idents.map((ident) => `${WELCOME_NS}.disabled.${encodeURIComponent(ident)}`);
}

function welcomeLoginEpochKeyForUser(user) {
  const ident = encodeURIComponent(welcomePrimaryIdentFromUser(user));
  return `${WELCOME_LOGIN_EPOCH_PREFIX}${ident}`;
}

function getWelcomeLoginEpoch(user) {
  try {
    const k = welcomeLoginEpochKeyForUser(user);
    return String(window.sessionStorage.getItem(k) || "").trim();
  } catch {
    return "";
  }
}

function bumpWelcomeLoginEpoch(user) {
  try {
    const k = welcomeLoginEpochKeyForUser(user);
    window.sessionStorage.setItem(k, String(Date.now()));
  } catch {}
}

function welcomeSessionSeenKeyForUser(user) {
  // ✅ Per-login “seen” key (epoch changes on each login)
  const ident = encodeURIComponent(welcomePrimaryIdentFromUser(user));
  const epoch = encodeURIComponent(getWelcomeLoginEpoch(user) || "0");
  return `${WELCOME_NS}.seenThisLogin.${ident}.${epoch}`;
}

function hasWelcomePreviewFlag() {
  try {
    return window.sessionStorage.getItem(WELCOME_PREVIEW_KEY) === "1";
  } catch {
    return false;
  }
}

function clearWelcomePreviewFlag() {
  try {
    window.sessionStorage.removeItem(WELCOME_PREVIEW_KEY);
  } catch {}
}

function isWelcomeDisabled(user) {
  try {
    // Back-compat: treat old "seen" key as "disabled" when present.
    if (window.localStorage.getItem(WELCOME_SEEN_KEY) === "1") return true;

    const keys = welcomeDisableKeysForUser(user);
    return keys.some((k) => window.localStorage.getItem(k) === "1");
  } catch {
    return false;
  }
}

function setWelcomeDisabled(user, yes) {
  try {
    const keys = welcomeDisableKeysForUser(user);

    // Write both (per-user) and (compat) keys so older builds still behave.
    if (yes) {
      keys.forEach((k) => window.localStorage.setItem(k, "1"));
      window.localStorage.setItem(WELCOME_SEEN_KEY, "1");
    } else {
      keys.forEach((k) => window.localStorage.removeItem(k));
      window.localStorage.removeItem(WELCOME_SEEN_KEY);
    }
  } catch {}
}

function hasSeenWelcomeThisLogin(user) {
  try {
    const k = welcomeSessionSeenKeyForUser(user);
    return window.sessionStorage.getItem(k) === "1";
  } catch {
    return false;
  }
}

function markWelcomeSeenThisLogin(user) {
  try {
    const k = welcomeSessionSeenKeyForUser(user);
    window.sessionStorage.setItem(k, "1");
  } catch {}
}

/* ------------------ AppShell ------------------ */

export default function AppShell() {
  const [activePage, setActivePage] = useState("projectHub");

  // Stabilise outer-page navigation before paint. Reading and manuscript
  // workspaces own their scroll restoration and must not be overridden here.
  const previousPageRef = useRef(activePage);
  useLayoutEffect(() => {
    const previous = previousPageRef.current;
    previousPageRef.current = activePage;
    if (previous === activePage) return;
    if (["readerView", "writerStudio", "childrensBookReader", "screenplayReader"].includes(activePage)) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [activePage]);

  const [projectHubContext, setProjectHubContext] =
    useState({
      category: "",
      projectId: "",
      projectTitle: "",
    });

  const [selectedForum, setSelectedForum] = useState("");

  // ✅ Storylines “open fresh” token (More menu -> Storylines)
  const [navResetToken, setNavResetToken] = useState(0);

  // Public profile target username (for /u/:username)
  const [publicUsername, setPublicUsername] = useState("");

  // Invite token state (for /invite?token=...)
  const [inviteToken, setInviteToken] = useState("");
  const [inviteAutoAccept, setInviteAutoAccept] = useState(false);

  const { booting, isAuthenticated, logout, user, isWriter, isAdmin, role } = useUser();

  const isLoggedIn = Boolean(isAuthenticated);
  const canSeeWriterStudio = Boolean(isLoggedIn && (isWriter || isAdmin));

  const projectHubPilot =
    projectHubNovelStoryPilotEnabled();

  const canSeeProjectHub =
    Boolean(
      projectHubPilot &&
      isLoggedIn
    );

  // Match Project Hub's existing eligible-writer rule. Do not grant rights
  // to general readers simply because Explore displays Textbooks.
  const canAccessTextbookStudio = Boolean(
    canSeeProjectHub &&
    (isWriter || isAdmin || user?.writerAccess === true)
  );

  const canSeeAdmin = Boolean(isLoggedIn && isAdmin);
  const canSeeEditorial = Boolean(isLoggedIn && (isAdmin || user?.contentSteward === true));

  const displayIdentity = useMemo(() => user?.name || user?.username || user?.email || "", [user]);

  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [notificationRefreshToken, setNotificationRefreshToken] = useState(0);

  function refreshNotificationCount() {
    setNotificationRefreshToken((x) => x + 1);
  }

  useEffect(() => {
    let alive = true;

    async function loadUnreadCount() {
      if (!isLoggedIn || booting) {
        if (alive) setNotificationUnreadCount(0);
        return;
      }

      try {
        const data = await api.getNotificationUnreadCount();
        if (!alive) return;
        setNotificationUnreadCount(Number(data?.unreadCount || 0));
      } catch {
        if (alive) setNotificationUnreadCount(0);
      }
    }

    loadUnreadCount();

    let timer = null;
    if (isLoggedIn && !booting) {
      timer = window.setInterval(loadUnreadCount, 60000);
    }

    return () => {
      alive = false;
      if (timer) window.clearInterval(timer);
    };
  }, [isLoggedIn, booting, notificationRefreshToken]);

  const [contactSuccess, setContactSuccess] = useState("");
  const [contactError, setContactError] = useState("");
  const [contactSending, setContactSending] = useState(false);
  
  // ✅ App language (EN/FR)
  const [appLang, setAppLangState] = useState("en");
  const t = TEXT[appLang] || TEXT.en;

  useEffect(() => {
    setAppLangState(getLangFromUrlOrStorage());
  }, []);

  function setLanguage(next) {
    const v = setAppLang(next);
    setAppLangState(v);

    // Keep URL clean: only keep ?lang=fr when actually on FR.
    try {
      const url = new URL(window.location.href);
      if (v === "fr") url.searchParams.set("lang", "fr");
      else url.searchParams.delete("lang");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    } catch {}
  }

  // ✅ Welcome / Orientation modal (per-login per-account, with permanent disable option)
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeDontShowAgain, setWelcomeDontShowAgain] = useState(false);

  // Track transitions so logout->login in same tab counts as a NEW login
  const prevLoggedInRef = useRef(false);

  function openWelcomeIfNeeded(force) {
    try {
      if (!isLoggedIn) return;
      if (booting) return;

      const preview = hasWelcomePreviewFlag();
      const shouldForce = Boolean(force) || preview;

      // If user disabled permanently, never show.
      // (Preview respects this too; user chose "never", we honor it.)
      if (isWelcomeDisabled(user)) {
        clearWelcomePreviewFlag();
        return;
      }

      const blockedPages = new Set([
        "login",
        "register",
        "verifyEmail",
        "resetPassword",
        "invite",
        "admin",
        "adminPanel",
        "adminUsers",
        "adminStories",
        "adminPublicHomepage",
        "adminWriterReviews",
        "adminContactInbox",
        "adminBetaFeedback",
        "accessDenied",
      ]);

      if (!shouldForce && blockedPages.has(String(activePage || ""))) return;

      // ✅ Per-login: if already seen in THIS login epoch, do nothing.
      // (Epoch changes on each successful login.)
      if (!shouldForce && hasSeenWelcomeThisLogin(user)) return;

      setWelcomeDontShowAgain(false);
      setWelcomeOpen(true);
    } catch {}
  }

  function closeWelcome(opts) {
    try {
      const disableForever = Boolean(opts?.disableForever);

      // Always mark seen for this login epoch when dismissed, so we don’t keep popping it up.
      markWelcomeSeenThisLogin(user);

      // Preview should never stick.
      clearWelcomePreviewFlag();

      if (disableForever) setWelcomeDisabled(user, true);
    } catch {}
    setWelcomeOpen(false);
  }

  // Beta Feedback (attached to account; stored via /api/feedback)
  const [betaOpen, setBetaOpen] = useState(false);
  const [betaBusy, setBetaBusy] = useState(false);
  const [betaOk, setBetaOk] = useState("");
  const [betaErr, setBetaErr] = useState("");

  const [betaUnclear, setBetaUnclear] = useState("");
  const [betaStrong, setBetaStrong] = useState("");
  const [betaHesitate, setBetaHesitate] = useState("");
  const [betaMore, setBetaMore] = useState("");

  function openBetaFeedback() {
    setBetaErr("");
    setBetaOk("");
    setBetaOpen(true);
  }

  function closeBetaFeedback() {
    if (betaBusy) return;
    setBetaOpen(false);
  }

  async function submitBetaFeedback() {
    if (betaBusy) return;

    const a = String(betaUnclear || "").trim();
    const b = String(betaStrong || "").trim();
    const c = String(betaHesitate || "").trim();
    const d = String(betaMore || "").trim();

    if (!a && !b && !c && !d) {
      setBetaErr("Please write at least one observation before submitting.");
      return;
    }

    setBetaBusy(true);
    setBetaErr("");
    setBetaOk("");

    try {
      const text =
        (a ? `What felt unclear?\n${a}\n\n` : "") +
        (b ? `What felt strong?\n${b}\n\n` : "") +
        (c ? `Where did you hesitate?\n${c}\n\n` : "") +
        (d ? `Additional observations\n${d}\n\n` : "");

      await api("/api/feedback", {
        method: "POST",
        body: JSON.stringify({
          text: String(text).slice(0, 2000),
          source: "beta_feedback_modal",
          target: "beta",
          parentId: "",
        }),
      });

      setBetaOk("Thank you. Your feedback has been submitted.");
      setBetaUnclear("");
      setBetaStrong("");
      setBetaHesitate("");
      setBetaMore("");

      setTimeout(() => {
        setBetaOpen(false);
        setBetaOk("");
      }, 600);
    } catch (e) {
      setBetaErr(String(e?.message || "Failed to submit feedback."));
    } finally {
      setBetaBusy(false);
    }
  }

  const roleLabel = useMemo(() => {
    if (!isLoggedIn) return "Guest";

    const normalizedRole = String(role || "user").trim().toLowerCase();

    if (normalizedRole === "user") return "Reader";
    if (normalizedRole === "writer" || normalizedRole === "power") {
      return "Writer";
    }
    if (normalizedRole === "moderator") return "Moderator";
    if (normalizedRole === "admin") return "Admin";

    return "Reader";
  }, [isLoggedIn, role]);

  function onNavigatePage(page, opts) {
    const p0 = String(page || "").trim();
    const p = p0 === "admin_contact_inbox" ? "adminContactInbox" : p0;

    // ✅ If Storylines is opened from More menu, force All Forums/All Writing Modes
    if (p === "storylines" && opts?.resetStorylines === true) {
      setSelectedForum("");
      setNavResetToken((t) => t + 1);
      try {
        window.localStorage.removeItem("ns.selectedForum");
      } catch {}
    }

    if (p === "projectHub") {
      const requestedProjectCategory =
        String(
          opts?.projectCategory || ""
        ).trim();

      const requestedProjectId =
        String(
          opts?.projectId ||
          opts?.storyId ||
          opts?.id ||
          ""
        ).trim();

      const requestedProjectTitle =
        String(
          opts?.projectTitle || ""
        ).trim();
      const requestedChapterId =
        String(opts?.chapterId || "").trim();

      if (
        requestedProjectCategory ||
        requestedProjectId
      ) {
        setProjectHubContext({
          category:
            requestedProjectCategory ||
            (requestedProjectId ? "story" : ""),
          projectId: requestedProjectId,
          projectTitle: requestedProjectTitle,
          chapterId: requestedProjectCategory === "children" ? requestedChapterId : "",
        });
      } else if (
        opts?.preserveProjectContext !== true
      ) {
        setProjectHubContext({
          category: "",
          projectId: "",
          projectTitle: "",
          chapterId: "",
        });
      }
    }

    setActivePage(p);
    page = p;


    try {
      const qs = String(window.location.search || "");

      const keepInviteQs =
        (page === "login" || page === "register") &&
        qs &&
        (qs.includes("inviteToken=") || qs.includes("token="));

      const keepLangQs = (page === "login" || page === "register") && appLang === "fr";

      // ✅ Reader View deep-link support + canonical URL
      if (page === "readerView") {
        const storyId = String(opts?.storyId || opts?.id || "").trim();
        const sceneId = String(opts?.sceneId || "").trim();
        const openValidated =
          opts?.openValidated === true ||
          String(opts?.openValidated || "").trim().toLowerCase() === "true" ||
          String(opts?.openValidated || "").trim() === "1";

        if (storyId) {
          try {
            window.localStorage.setItem("ns.reader.focusStoryId", storyId);
            window.localStorage.setItem("ns.reader.lastStoryId", storyId);
          } catch {}
        }

        const params = new URLSearchParams();
        if (storyId) params.set("storyId", storyId);
        if (sceneId) params.set("sceneId", sceneId);
        if (openValidated) params.set("openValidated", "1");

        const query = params.toString();
        window.history.replaceState({}, "", query ? `/reader-view?${query}` : "/reader-view");
        return;
      }

      if (page === "writerStudio") {
        const storyId =
          String(
            opts?.storyId ||
            opts?.id ||
            ""
          ).trim();

        const chapterId =
          String(opts?.chapterId || "").trim();

        const sceneId =
          String(opts?.sceneId || "").trim();

        const newScene =
          opts?.newScene === true ||
          String(opts?.newScene || "").trim() === "1";

        const demoPreview =
          opts?.demoPreview === true ||
          String(opts?.demoPreview || "").trim() === "1";

        const previewSceneTitle =
          String(opts?.previewSceneTitle || "").trim();

        const previewSceneText =
          String(opts?.previewSceneText || "");

        const previewChapterTitle =
          String(opts?.previewChapterTitle || "").trim();

        if (storyId && !demoPreview) {
          try {
            window.localStorage.setItem(
              "ns.writerStudio.focusStoryId",
              storyId
            );

            window.localStorage.setItem(
              "ns.writerStudio.storyId",
              storyId
            );

            window.localStorage.setItem(
              "ns.selectedStoryId",
              storyId
            );

            if (chapterId) {
              window.localStorage.setItem(
                "ns.writerStudio.chapterId",
                chapterId
              );
            } else {
              window.localStorage.removeItem(
                "ns.writerStudio.chapterId"
              );
            }

            if (sceneId) {
              window.localStorage.setItem(
                "ns.writerStudio.sceneId",
                sceneId
              );
            } else {
              window.localStorage.removeItem(
                "ns.writerStudio.sceneId"
              );
            }
          } catch {}
        }

        const params = new URLSearchParams();
        if (storyId) params.set("storyId", storyId);
        if (chapterId) params.set("chapterId", chapterId);
        if (sceneId) params.set("sceneId", sceneId);
        if (newScene) params.set("newScene", "1");
        if (demoPreview) params.set("demoPreview", "1");
        if (previewSceneTitle) {
          params.set("previewSceneTitle", previewSceneTitle);
        }
        if (previewSceneText) {
          params.set("previewSceneText", previewSceneText);
        }
        if (previewChapterTitle) {
          params.set(
            "previewChapterTitle",
            previewChapterTitle
          );
        }

        const query = params.toString();
        const url = query
          ? `/writerStudio?${query}`
          : "/writerStudio";

        window.history.replaceState(
          {},
          "",
          url
        );

        // Writer Studio is a focused workspace. Do not inherit
        // the Book Structure page's previous vertical scroll,
        // which makes the handoff appear to jump or "bump".
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });

        return;
      }

      if (page === "projectHub") {
        const requestedProjectCategory =
          String(
            opts?.projectCategory || ""
          )
            .trim()
            .toLowerCase();

        const requestedProjectId =
          String(
            opts?.projectId ||
            opts?.storyId ||
            opts?.id ||
            ""
          ).trim();

        const requestedBookSection =
          String(opts?.bookSection || "")
            .trim()
            .toLowerCase();

        const storyBookSectionQuery =
          requestedProjectCategory === "story" &&
          (requestedBookSection === "front" ||
            requestedBookSection === "back")
            ? `&bookSection=${requestedBookSection}`
            : "";

        const targetPath =
          requestedProjectCategory ===
          "children"
            ? "/my-projects/childrens-book"
            : requestedProjectCategory ===
              "textbook"
            ? "/my-projects/textbook"
            : requestedProjectCategory ===
              "screenplay"
            ? "/my-projects/screenplay"
            : requestedProjectId
            ? `/my-projects?storyId=${encodeURIComponent(
                requestedProjectId
              )}${storyBookSectionQuery}`
            : "/my-projects";

        window.history.replaceState(
          {},
          "",
          targetPath
        );

        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });

        return;
      }

      if (page === "storyDashboard") {
        const storyId = String(opts?.storyId || opts?.id || "").trim();

        if (storyId) {
          try {
            window.localStorage.setItem("ns.writer.focusStoryId", storyId);
            window.localStorage.setItem("ns.selectedStoryId", storyId);
          } catch {}
        }

        const url = storyId ? `/story-dashboard?storyId=${encodeURIComponent(storyId)}` : "/story-dashboard";
        window.history.replaceState({}, "", url);
        return;
      }

      if (page === "editorialBoard") {
        const storyId = String(opts?.storyId || opts?.id || "").trim();

        if (storyId) {
          try {
            window.localStorage.setItem("ns.writer.focusStoryId", storyId);
            window.localStorage.setItem("ns.selectedStoryId", storyId);
          } catch {}
        }

        const url = storyId ? `/editorial-board?storyId=${encodeURIComponent(storyId)}` : "/editorial-board";
        window.history.replaceState({}, "", url);
        return;
      }

      // Public profile URL support
      if (page === "publicProfile") {
        const uname = String(opts?.username || "").trim().toLowerCase();
        setPublicUsername(uname);
        const url = uname ? `/u/${encodeURIComponent(uname)}` : "/u";
        window.history.replaceState({}, "", url);
        return;
      }

      // Invite page support
      if (page === "invite") {
        const t = String(opts?.token || inviteToken || "").trim();
        const auto = opts?.autoAccept === true;
        setInviteToken(t);
        setInviteAutoAccept(auto);

        const url = t ? `/invite?token=${encodeURIComponent(t)}` : "/invite";
        window.history.replaceState({}, "", url);
        return;
      }

      // Screenplay reader shelf
      if (page === "screenplayDiscovery") {
        window.history.replaceState(
          {},
          "",
          "/reader/screenplay"
        );
        return;
      }

      // Children's Book reader shelf
      if (page === "childrensBookDiscovery") {
        window.history.replaceState(
          {},
          "",
          "/reader/childrens-book"
        );
        return;
      }

      // My Shelf page
      if (page === "myShelf") {
        window.history.replaceState({}, "", "/my-shelf");
        return;
      }

      // Private profile page
      if (page === "profile") {
        window.history.replaceState({}, "", "/profile");
        return;
      }

      // Notifications page
      if (page === "notifications") {
        window.history.replaceState({}, "", "/notifications");
        return;
      }

      // Admin Contact Inbox (keep URL stable + human-readable)
      if (page === "adminContactInbox") {
        window.history.replaceState({}, "", "/admin-contact");
        return;
      }

      if (page === "adminPublicHomepage") {
        window.history.replaceState({}, "", "/admin-public-homepage");
        return;
      }
      if (page === "contentSteward") {
        window.history.replaceState({}, "", "/content-steward");
        return;
      }

      if (page === "adminWriterReviews") {
        window.history.replaceState({}, "", "/admin-writer-reviews");
        return;
      }

      // ✅ preserve query string for invite + language only where needed
      const baseUrl = page === "home" ? "/" : `/${page}`;

      if (page === "login" || page === "register") {
        const url = new URL(window.location.href);
        const next = new URL(`${baseUrl}`, url.origin);

        if (keepInviteQs) {
          const cur = new URLSearchParams(url.search || "");
          const token = cur.get("token");
          const inviteToken2 = cur.get("inviteToken");
          if (token) next.searchParams.set("token", token);
          if (inviteToken2) next.searchParams.set("inviteToken", inviteToken2);
        }

        // Language param should reflect CURRENT UI language only.
// Fix: avoid sticky ?lang=fr when user has switched back to English.
        if (keepLangQs && appLang === "fr") next.searchParams.set("lang", "fr");
        else next.searchParams.delete("lang");

        window.history.replaceState({}, "", `${next.pathname}${next.search}`);
        return;
      }

      window.history.replaceState({}, "", baseUrl);
    } catch {}
  }

  useEffect(() => {
    if (!isLoggedIn) clearGuestAuthArtifacts();
  }, [isLoggedIn]);

  // ✅ NEW: bump per-login epoch on each successful login transition (false -> true)
  useEffect(() => {
    const prev = Boolean(prevLoggedInRef.current);
    const now = Boolean(isLoggedIn);

    if (!prev && now) {
      // New login occurred (or session restored into authenticated state after being unauthenticated)
      bumpWelcomeLoginEpoch(user);
    }

    prevLoggedInRef.current = now;
  }, [isLoggedIn, user]);

  // ✅ Show Welcome panel after a successful login (once per login, unless disabled)
  useEffect(() => {
    if (!isLoggedIn) return;
    if (booting) return;
    openWelcomeIfNeeded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, booting]);

  // ✅ If Profile sets preview flag, open immediately (even if already seen this login)
  useEffect(() => {
    if (!isLoggedIn) return;
    if (booting) return;
    if (!hasWelcomePreviewFlag()) return;
    if (welcomeOpen) return;
    openWelcomeIfNeeded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, booting, activePage, welcomeOpen]);

  // URL -> activePage (deep-link)
  useEffect(() => {
    try {
      const rawPath = String(window.location.pathname || "/");
      const path = rawPath.toLowerCase();
      const q = String(window.location.search || "");

      // ✅ apply language from URL once (if present)
      try {
        const qs = new URLSearchParams(window.location.search || "");
        const qLang = String(qs.get("lang") || "").trim().toLowerCase();
        if (qLang === "fr" || qLang === "en") setLanguage(qLang);
      } catch {}

      // Invite route: /invite?token=...
      if (path === "/invite") {
        const t = parseInviteTokenFromLocation();
        setInviteToken(t);
        setInviteAutoAccept(false);
        setActivePage("invite");
        return;
      }

      // Public profile route: /u/:username
      if (path === "/u" || path.startsWith("/u/")) {
        const uname = parsePublicUsernameFromPathname(rawPath);
        setPublicUsername(uname);
        setActivePage("publicProfile");
        return;
      }

      if (path === "/adminusers" || path === "/adminusers/") {
        window.history.replaceState({}, "", "/admin-users");
        setActivePage("adminUsers");
        return;
      }

      if (path.startsWith("/reset-password")) {
        setActivePage("resetPassword");
        return;
      }
      if (path.startsWith("/login")) {
        setActivePage("login");
        return;
      }
      if (path.startsWith("/register")) {
        setActivePage("register");
        return;
      }
      if (path.startsWith("/verify-email")) {
        setActivePage("verifyEmail");
        return;
      }

      // ✅ canonicalize ReaderView path (keep query string)
      if (path === "/readerview" || path === "/reader-view") {
        window.history.replaceState({}, "", `/reader-view${q}`);
        setActivePage("readerView");
        return;
      }

        if (
          path ===
            "/reader/screenplay"
        ) {
          setActivePage(
            "screenplayDiscovery"
          );
          return;
        }

        if (
          path.startsWith(
            "/reader/screenplay/"
          )
        ) {
          setActivePage(
            "screenplayReader"
          );
          return;
        }

        if (
          path ===
            "/reader/childrens-book"
        ) {
          setActivePage(
            "childrensBookDiscovery"
          );
          return;
        }

        if (
          path.startsWith(
            "/reader/childrens-book/"
          )
        ) {
          setActivePage(
            "childrensBookReader"
          );
          return;
        }

      if (
        path === "/myprojects" ||
        path === "/my-projects" ||
        path === "/projecthub" ||
        path === "/project-hub" ||
        path.startsWith(
          "/my-projects/childrens-book"
        ) ||
        path.startsWith(
          "/my-projects/textbook"
        ) ||
        path.startsWith(
          "/my-projects/screenplay"
        )
      ) {
        const isChildrensBookPath =
          path.startsWith(
            "/my-projects/childrens-book"
          );

        const isTextbookPath =
          path.startsWith(
            "/my-projects/textbook"
          );

        const isScreenplayPath =
          path.startsWith(
            "/my-projects/screenplay"
          );

        if (isChildrensBookPath) {
          setProjectHubContext({
            category: "children",
            projectId: "",
            projectTitle: "",
          });
        } else if (isTextbookPath) {
          setProjectHubContext({
            category: "textbook",
            projectId: "",
            projectTitle: "",
          });
        } else if (isScreenplayPath) {
          setProjectHubContext({
            category: "screenplay",
            projectId: "",
            projectTitle: "",
          });
        } else {
          const params =
            new URLSearchParams(q || "");

          const storyProjectId =
            String(
              params.get("storyId") ||
              params.get("projectId") ||
              ""
            ).trim();

          if (storyProjectId) {
            setProjectHubContext({
              category: "story",
              projectId: storyProjectId,
              projectTitle: "",
            });
          }

          window.history.replaceState(
            {},
            "",
            `/my-projects${q}`
          );
        }

        setActivePage(
          projectHubNovelStoryPilotEnabled()
            ? "projectHub"
            : "home"
        );
        return;
      }

      if (path === "/storydashboard" || path === "/story-dashboard") {
        window.history.replaceState({}, "", `/story-dashboard${q}`);
        setActivePage("storyDashboard");
        return;
      }

      if (path === "/editorialboard" || path === "/editorial-board") {
        window.history.replaceState({}, "", `/editorial-board${q}`);
        setActivePage("editorialBoard");
        return;
      }

      if (path === "/writerstudio" || path === "/writer-studio") {
        setActivePage("writerStudio");
        return;
      }
      if (path === "/storyhub" || path === "/story-hub") {
        setActivePage("storyHub");
        return;
      }
      if (path === "/forums") {
        setActivePage("forums");
        return;
      }
      if (path === "/storylines") {
        setActivePage("storylines");
        return;
      }
      if (path === "/myshelf" || path === "/my-shelf") {
        setActivePage("myShelf");
        return;
      }
      if (path === "/profile") {
        setActivePage("profile");
        return;
      }
      if (path === "/notifications") {
        setActivePage("notifications");
        return;
      }
      if (path === "/howitworks" || path === "/how-it-works") {
        setActivePage("howItWorks");
        return;
      }
      if (path === "/mystery250") {
        setActivePage("mystery250");
        return;
      }

      if (path === "/faq") {
        setActivePage("faq");
        return;
      }
      if (path === "/blog") {
        setActivePage("blog");
        return;
      }
      if (path === "/contact") {
        setActivePage("contact");
        return;
      }
      if (path === "/terms") {
        setActivePage("terms");
        return;
      }
      if (path === "/privacy") {
        setActivePage("privacy");
        return;
      }

      if (path === "/admin") {
        setActivePage("admin");
        return;
      }
      if (path === "/admin-panel") {
        setActivePage("adminPanel");
        return;
      }
      if (path === "/admin-users") {
        setActivePage("adminUsers");
        return;
      }
      if (path === "/admin-stories") {
        setActivePage("adminStories");
        return;
      }
      if (path === "/admin-public-homepage" || path === "/adminpublichomepage") {
        setActivePage("adminPublicHomepage");
        return;
      }
      if (path === "/admin-writer-reviews" || path === "/adminwriterreviews") {
        setActivePage("adminWriterReviews");
        return;
      }
      if (path === "/content-steward") {
        setActivePage("contentSteward");
        return;
      }
      if (path === "/admin-contact" || path === "/admin-contact-inbox") {
        setActivePage("adminContactInbox");
        return;
      }

      if (path === "/" || path === "") {
        setActivePage("projectHub");
        return;
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Authenticated readers retain Storylines; writers go directly to existing Project Hub.
  // Guests continue through the existing login guard. No duplicate landing page.
  useEffect(() => {
    if (booting || !isLoggedIn || activePage !== "projectHub") return;
    if (window.location.pathname !== "/") return;
    if (!isWriter && !isAdmin && user?.writerAccess !== true) {
      onNavigatePage("storylines");
    }
    // Navigation is intentionally guarded by the existing route and role state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booting, isLoggedIn, activePage, isWriter, isAdmin, user?.writerAccess]);

  // Permission map
  const pageRules = useMemo(() => {
    return {
      profile: { auth: true },
      notifications: { auth: true },
      writerStudio: { auth: true, role: "writer" },
      projectHub: { auth: true },
      storyDashboard: { auth: true, role: "writer" },
      editorialBoard: { auth: true, role: "writer" },
      admin: { auth: true, role: "admin" },
      adminPanel: { auth: true, role: "admin" },
      adminUsers: { auth: true, role: "admin" },
      adminStories: { auth: true, role: "admin" },
      adminPublicHomepage: { auth: true, role: "admin" },
      contentSteward: { auth: true, role: "contentSteward" },
      adminWriterReviews: { auth: true, role: "admin" },
      adminContactInbox: { auth: true, role: "admin" },
    };
  }, []);

  // Central guard
  useEffect(() => {
    if (booting) return;

    const rule = pageRules[activePage];
    if (!rule) return;

    if (rule.auth && !isLoggedIn) {
      setPostLoginTarget(activePage, projectHubContext.category);
      onNavigatePage("login");
      return;
    }

    const reqRole = String(rule.role || "").toLowerCase();
    if (!reqRole) return;

    const r = String(role || "").toLowerCase();

    if (
      activePage === "writerStudio" ||
      activePage === "storyDashboard" ||
      activePage === "editorialBoard"
    ) {
      if (!(r === "writer" || r === "moderator" || r === "admin")) {
        onNavigatePage("accessDenied");
      }
      return;
    }

    if (reqRole === "contentSteward") {
      if (!canSeeEditorial) onNavigatePage("accessDenied");
      return;
    }

    if (reqRole === "admin") {
      if (r !== "admin") {
        onNavigatePage("accessDenied");
      }
    }
  }, [activePage, booting, isLoggedIn, role, pageRules, canSeeEditorial]);

  const forumsBreadcrumb = useMemo(() => [{ label: "HOME", page: "home" }, { label: "FORUMS", page: null }], []);

  // Content blocks (FAQ/Terms/Privacy moved to ./content/*; Blog/Contact remain inline)
  const blogContent = useMemo(() => {
    return {
      title: "NextScenes Blog",
      effectiveDate: "",
      intro:
        "Welcome to the NextScenes Blog. This is where we share updates, announcements, development notes, and reflections on storytelling and collaborative creativity.",
      sections: [
        {
          heading: "Coming Soon",
          paragraphs: [
            "This blog is intentionally simple for now. Soon you’ll see platform updates, roadmap notes, writing insights, and spotlights on storylines and mystery projects.",
          ],
        },
        {
          heading: "What you will find here",
          bullets: [
            "New features and platform releases",
            "Announcements and roadmaps",
            "Writing craft notes and storytelling insights",
            "Community spotlights and featured projects",
          ],
        },
        {
          heading: "A short note",
          paragraphs: [
            "A platform is like a village square. It becomes meaningful when people gather with purpose. This page will grow with the community.",
          ],
        },
      ],
    };
  }, []);

  const contactContent = useMemo(() => {
    return {
      title: "Contact NextScenes",
      effectiveDate: "",
      intro:
        "We’re glad you’re here. If you have questions, suggestions, or problems to report, you can reach the NextScenes team using the information below.",
      sections: [
        { heading: "General Contact", paragraphs: ["Email: [add your support email here]"] },
        {
          heading: "What you can contact us about",
          bullets: [
            "Account or login problems",
            "Reporting bugs or technical issues",
            "Content moderation concerns",
            "Feature suggestions and feedback",
            "Partnership or collaboration proposals",
          ],
        },
        {
          heading: "Response Time",
          paragraphs: [
            "NextScenes is a growing platform. We do our best to respond, but please allow time for replies. Clear messages get the fastest answers.",
          ],
        },
        { heading: "Closing", paragraphs: ["Every serious project begins as a conversation. We’re listening."] },
      ],
    };
  }, []);

  function renderMain() {
    const showBoot = booting === true;

    switch (activePage) {
      case "home":
        return <Home onNavigatePage={onNavigatePage} />;

      case "forums":
        return (
          <Forums
            onNavigatePage={onNavigatePage}
            breadcrumb={forumsBreadcrumb}
            onSelectForum={(name) => {
              setSelectedForum(name);
              onNavigatePage("storylines");
            }}
          />
        );

      case "storylines":
        return (
          <Storylines onNavigatePage={onNavigatePage} selectedForum={selectedForum} navResetToken={navResetToken} />
        );

      case "myShelf":
        return <MyShelf onNavigatePage={onNavigatePage} />;

      case "notifications":
  if (showBoot) return <BootScreen />;
  return (
    <Notifications
      onNavigatePage={onNavigatePage}
      onNotificationsChanged={refreshNotificationCount}
      lang={appLang}
    />
  );

      case "writerStudio":
        if (showBoot) return <BootScreen />;
        return (
          <WriterStudio
            onNavigatePage={onNavigatePage}
            projectHubContext={projectHubContext}
          />
        );

      case "readerView":
        return (
          <ReaderView
            onNavigatePage={onNavigatePage}
            projectHubContext={projectHubContext}
          />
        );

      case "screenplayDiscovery":
        return (
          <ScreenplayDiscovery />
        );

      case "screenplayReader":
        return (
          <ScreenplayReaderView />
        );

        case "childrensBookDiscovery":
          return (
            <ChildrensBookDiscovery />
          );

        case "childrensBookReader":
          return (
            <ChildrensBookReader
              onNavigatePage={onNavigatePage}
            />
          );

      case "projectHub":
        if (showBoot) return <BootScreen />;

        if (!projectHubPilot) {
          return <Home onNavigatePage={onNavigatePage} />;
        }

        return (
          <ProjectHub
            onNavigatePage={onNavigatePage}
            onContextChange={setProjectHubContext}
            initialCategory={
              projectHubContext.category
            }
            initialProjectId={
              projectHubContext.projectId
            }
            initialChapterId={
              projectHubContext.chapterId || ""
            }
          />
        );

      case "storyDashboard":
        if (showBoot) return <BootScreen />;
        return (
          <StoryDashboard
            onNavigatePage={onNavigatePage}
            projectHubContext={projectHubContext}
          />
        );

      case "editorialBoard":
        if (showBoot) return <BootScreen />;
        return (
          <EditorialBoard
            onNavigatePage={onNavigatePage}
            projectHubContext={projectHubContext}
          />
        );

      case "profile":
        return <Profile onNavigatePage={onNavigatePage} />;

      case "publicProfile":
        return <PublicProfile onNavigatePage={onNavigatePage} username={publicUsername} />;

      case "storyHub":
        return (
          <StoryHub
            onNavigatePage={onNavigatePage}
            onEnterWriterRoom={(story) => {
              const storyId = String(story?._id || story?.id || story?.storyId || "").trim();
              if (storyId) {
                try {
                  window.localStorage.setItem("ns.writer.focusStoryId", storyId);
                  window.localStorage.setItem("ns.selectedStoryId", storyId);
                } catch {}
              }
              onNavigatePage("writerStudio", storyId ? { storyId } : undefined);
            }}
            onOpenReaderView={(story) => {
              const storyId = String(story?._id || story?.id || story?.storyId || "").trim();
              if (storyId) {
                try {
                  window.localStorage.setItem("ns.reader.focusStoryId", storyId);
                  window.localStorage.setItem("ns.reader.lastStoryId", storyId);
                  window.localStorage.setItem("ns.selectedStoryId", storyId);
                } catch {}
              }
              onNavigatePage("readerView", storyId ? { storyId } : undefined);
            }}
            lang={appLang}
            projectHubContext={projectHubContext}
          />
        );

      case "mystery250":
        return <Mystery250 onNavigatePage={onNavigatePage} />;

      case "howItWorks":
        return <HowItWorks onNavigatePage={onNavigatePage} />;

      case "faq":
        return (
          <PolicyHubPage
            title="FAQ"
            intro="For beta, the FAQ is maintained on the public site so it stays consistent."
            href={`${PUBLIC_SITE_URL}/faq`}
            onGo={onNavigatePage}
          />
        );

      case "blog":
        return <LegalPage {...blogContent} onGo={onNavigatePage} />;

      case "contact":
  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 820, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0 }}>Contact NextScenes</h2>

        <p style={{ marginTop: 8, color: "#475569" }}>
          We’re here to help. Send us a message below.
        </p>

      {contactError ? (
  <div
    style={{
      marginTop: 12,
      padding: "10px 12px",
      borderRadius: 10,
      background: "#fef2f2",
      border: "1px solid #fecaca",
      color: "#991b1b",
      fontWeight: 600,
    }}
  >
    {contactError}
  </div>
) : null}

{contactSuccess ? (
  <div
    style={{
      marginTop: 12,
      padding: "10px 12px",
      borderRadius: 10,
      background: "#ecfdf5",
      border: "1px solid #a7f3d0",
      color: "#065f46",
      fontWeight: 600,
    }}
  >
    {contactSuccess}
  </div>
) : null}

        <form
          onSubmit={async (e) => {
  e.preventDefault();

  setContactError("");
  setContactSuccess("");

  const form = new FormData(e.target);

  const payload = {
    name: form.get("name"),
    email: form.get("email"),
    subject: form.get("subject"),
    message: form.get("message"),
    honey: "",
    pageUrl: window.location.href,
  };

  if (!payload.name || !payload.email || !payload.subject || !payload.message) {
    setContactError("Please complete all required fields.");
    return;
  }

  try {
    setContactSending(true);

    const res = await api("/api/contact", {
      method: "POST",
      body: JSON.stringify(payload),
    });

    if (res?.ok) {
      setContactSuccess("Message received. We will get back to you shortly.");

      const subjectInput = e.target.elements.namedItem("subject");
      const messageInput = e.target.elements.namedItem("message");

      if (subjectInput) subjectInput.value = "";
      if (messageInput) messageInput.value = "";
    } else {
      setContactError(res?.error || "Something went wrong.");
    }
  } catch {
    setContactError("Failed to send message. Please try again.");
  } finally {
    setContactSending(false);
  }
}}
          style={{ marginTop: 16 }}
        >
          <input
  className="ns-input"
  name="name"
  placeholder="Your name"
  defaultValue={user?.name || user?.username || ""}
/>
          <input
  className="ns-input"
  name="email"
  placeholder="Your email"
  defaultValue={user?.email || ""}
  style={{ marginTop: 10 }}
/>
          <input
            className="ns-input"
            name="subject"
            placeholder="Subject"
            style={{ marginTop: 10 }}
          />
          <textarea
            className="ns-input"
            name="message"
            placeholder="Your message"
            style={{ marginTop: 10, minHeight: 140 }}
          />

          <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-start" }}>
  <button
    type="submit"
    className="ns-button ns-button-primary"
    disabled={contactSending}
  >
    {contactSending ? "Sending..." : "Send Message"}
  </button>
</div>
        </form>

        <div style={{ marginTop: 24 }}>
  <h3 style={{ marginBottom: 10 }}>General Contact</h3>
  <p style={{ color: "#475569", margin: 0 }}>
    Email: <strong>support@nextscenes.org</strong>
  </p>

  <h3 style={{ marginTop: 22, marginBottom: 10 }}>What you can contact us about</h3>
  <ul style={{ color: "#475569", lineHeight: 1.8, marginTop: 0, paddingLeft: 22 }}>
    <li>Account or login problems</li>
    <li>Reporting bugs or technical issues</li>
    <li>Content moderation concerns</li>
    <li>Feature suggestions and feedback</li>
    <li>Partnership or collaboration proposals</li>
  </ul>

  <h3 style={{ marginTop: 22, marginBottom: 10 }}>Response Time</h3>
  <p style={{ color: "#475569", margin: 0 }}>
    NextScenes is a growing platform. We do our best to respond, but please allow time for replies.
    Clear messages get the fastest answers.
  </p>

  <h3 style={{ marginTop: 22, marginBottom: 10 }}>Closing</h3>
  <p style={{ color: "#475569", margin: 0 }}>
    Every serious project begins as a conversation. We’re listening.
  </p>
</div>
      </div>
    </div>
  );

      case "terms":
        return (
          <PolicyHubPage
            title="Terms of Service"
            intro="For beta, Terms are maintained on the public site to keep a single canonical version."
            href={`${PUBLIC_SITE_URL}/terms`}
            onGo={onNavigatePage}
          />
        );

      case "privacy":
        return (
          <PolicyHubPage
            title="Privacy Policy"
            intro="For beta, Privacy is maintained on the public site to keep a single canonical version."
            href={`${PUBLIC_SITE_URL}/privacy`}
            onGo={onNavigatePage}
          />
        );

      case "login":
        return <Login onNavigatePage={onNavigatePage} lang={appLang} />;

      case "register":
        return <Register onNavigatePage={onNavigatePage} lang={appLang} />;

      case "resetPassword":
        return <ResetPassword onNavigatePage={onNavigatePage} />;

      case "verifyEmail":
        return <VerifyEmail onNavigatePage={onNavigatePage} />;

      case "invite":
        return <InviteAccept onNavigatePage={onNavigatePage} token={inviteToken} autoAccept={inviteAutoAccept} />;

      case "welcome":
        return <Welcome onGoHome={() => onNavigatePage("projectHub")} />;

      case "admin":
        if (showBoot) return <BootScreen />;
        return <AdminDashboard onGo={onNavigatePage} />;

      case "adminPanel":
        if (showBoot) return <BootScreen />;
        return <AdminPanel onNavigate={onNavigatePage} />;

      case "adminUsers":
        if (showBoot) return <BootScreen />;
        return <AdminUsersPage onNavigate={onNavigatePage} onGo={onNavigatePage} />;

      case "adminStories":
        if (showBoot) return <BootScreen />;
        return <AdminStories onGo={onNavigatePage} />;

      case "adminPublicHomepage":
        if (showBoot) return <BootScreen />;
        return <AdminPublicHomepage lang={appLang} />;

      case "contentSteward":
        if (showBoot) return <BootScreen />;
        return <ContentStewardWorkspaceV1 lang={appLang} />;

      case "adminWriterReviews":
        if (showBoot) return <BootScreen />;
        return <AdminWriterReviews onNavigate={onNavigatePage} onGo={onNavigatePage} />;

      case "adminBetaFeedback":
        if (showBoot) return <BootScreen />;
        return <AdminBetaFeedback onGo={onNavigatePage} />;

      case "adminContactInbox":
        if (showBoot) return <BootScreen />;
        return <AdminContactInbox onGo={onNavigatePage} />;

      case "accessDenied":
        return (
          <AccessDenied
            onGo={onNavigatePage}
            title="Access denied"
            message="You are logged in, but your role does not allow access to that page."
          />
        );

      default:
        return <Home onNavigatePage={onNavigatePage} />;
    }
  }

  return (
    <div className="ns-app">
      <TopNav
        activePage={activePage}
        onGo={onNavigatePage}
        isAuthenticated={isLoggedIn}
        logout={logout}
        canSeeWriterStudio={canSeeWriterStudio}
        canSeeProjectHub={canSeeProjectHub}
        canAccessTextbookStudio={canAccessTextbookStudio}
        canSeeAdmin={canSeeAdmin}
        canSeeEditorial={canSeeEditorial}
        onOpenBetaFeedback={openBetaFeedback}
        lang={appLang}
        onSetLang={setLanguage}
        notificationUnreadCount={notificationUnreadCount}
        projectHubContext={projectHubContext}
      />

           {/* ✅ Welcome / Orientation modal */}
      {welcomeOpen ? (
        <div
          className="ns-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(2, 6, 23, 0.55)",
            zIndex: 2200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 14,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeWelcome({ disableForever: welcomeDontShowAgain });
          }}
        >
          <div
            className="ns-card"
            style={{
              width: "min(720px, 100%)",
              borderRadius: 16,
              padding: 18,
              background: "white",
              boxShadow: "0 18px 48px rgba(0,0,0,0.28)",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>{t.welcome_title}</div>
                <div style={{ marginTop: 6, fontSize: 13, color: "#475569", lineHeight: 1.5 }}>
                  {t.welcome_intro}
                </div>
              </div>

              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() => closeWelcome({ disableForever: welcomeDontShowAgain })}
                title={t.btn_close}
              >
                {t.btn_close}
              </button>
            </div>

            <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: 12 }}>
                <div style={{ fontWeight: 800, color: "#0f172a" }}>{t.choose_path}</div>
                <div style={{ marginTop: 6, color: "#475569", lineHeight: 1.7 }}>
                  <div>
                    <strong>Story Hub</strong> → {t.welcome_storyhub_desc}
                  </div>
                  <div>
                    <strong>Writer Studio</strong> → {t.welcome_writer_desc}
                  </div>
                  <div>
                    <strong>Reader View</strong> → {t.welcome_reader_desc}
                  </div>
                </div>
              </div>

              <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: 12 }}>
                <div style={{ fontWeight: 800, color: "#0f172a" }}>{t.small_note}</div>
                <div style={{ marginTop: 6, color: "#475569", lineHeight: 1.6 }}>
                  {t.welcome_note_body}
                </div>
              </div>
            </div>

            <label style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", color: "#334155" }}>
              <input
                type="checkbox"
                checked={welcomeDontShowAgain}
                onChange={(e) => setWelcomeDontShowAgain(Boolean(e?.target?.checked))}
              />
              <span style={{ fontSize: 13 }}>{t.dont_show_greeting}</span>
            </label>

            <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() => {
                  closeWelcome({ disableForever: welcomeDontShowAgain });
                  if (canSeeProjectHub) {
                    onNavigatePage("projectHub", { projectCategory: "story" });
                  } else {
                    // Preserve Reader discovery and existing-story access.
                    onNavigatePage("storyHub");
                  }
                }}
              >
                {canSeeProjectHub
                  ? (appLang === "fr" ? "Ouvrir mes projets Roman / Histoire" : "Open Novel / Story Projects")
                  : t.open_storyhub}
              </button>

              {canSeeWriterStudio ? (
                <button
                  type="button"
                  className="ns-button ns-button-outline"
                  onClick={() => {
                    try {
                      const remembered = String(
                        window.localStorage.getItem("ns.reader.lastStoryId") ||
                          window.localStorage.getItem("ns.selectedStoryId") ||
                          ""
                      ).trim();
                      if (remembered) {
                        window.localStorage.setItem("ns.writer.focusStoryId", remembered);
                        window.localStorage.setItem("ns.selectedStoryId", remembered);
                      }
                    } catch {}
                    closeWelcome({ disableForever: welcomeDontShowAgain });
                    onNavigatePage("writerStudio");
                  }}
                >
                  {String(
                    window.localStorage.getItem("ns.reader.lastStoryId") ||
                      window.localStorage.getItem("ns.selectedStoryId") ||
                      ""
                  ).trim()
                    ? t.continue_writer
                    : t.open_writer}
                </button>
              ) : null}

              <button
                type="button"
                className="ns-button ns-button-primary"
                onClick={() => {
                  closeWelcome({ disableForever: welcomeDontShowAgain });
                  onNavigatePage("readerView");
                }}
              >
                {t.open_reader}
              </button>
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
              {t.signed_in_as}: <b>{displayIdentity || "User"}</b>
            </div>
          </div>
        </div>
      ) : null}

      {/* ✅ Beta Feedback modal */}
      {betaOpen ? (
        <div
          className="ns-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(2, 6, 23, 0.55)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 14,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeBetaFeedback();
          }}
        >
          <div
            className="ns-card"
            style={{
              width: "min(720px, 100%)",
              borderRadius: 16,
              padding: 18,
              background: "white",
              boxShadow: "0 18px 48px rgba(0,0,0,0.28)",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Beta Feedback</div>
                <div style={{ marginTop: 4, fontSize: 13, color: "#475569" }}>
                  Your notes help us refine clarity, safety, and flow. No clout. No noise. Just improvement.
                </div>
              </div>

              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={closeBetaFeedback}
                disabled={betaBusy}
                title="Close"
              >
                Close
              </button>
            </div>

            {betaErr ? (
              <div style={{ marginTop: 12 }} className="ns-alert ns-alert-error">
                {betaErr}
              </div>
            ) : null}
            {betaOk ? (
              <div style={{ marginTop: 12 }} className="ns-alert ns-alert-success">
                {betaOk}
              </div>
            ) : null}

            <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              <label style={{ display: "block" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>
                  What felt unclear?
                </div>
                <textarea
                  className="ns-input"
                  style={{ minHeight: 70, resize: "vertical" }}
                  value={betaUnclear}
                  onChange={(e) => setBetaUnclear(e.target.value)}
                  placeholder="Anything that made you pause, reread, or wonder what happens next."
                  maxLength={700}
                />
              </label>

              <label style={{ display: "block" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>
                  What felt strong?
                </div>
                <textarea
                  className="ns-input"
                  style={{ minHeight: 70, resize: "vertical" }}
                  value={betaStrong}
                  onChange={(e) => setBetaStrong(e.target.value)}
                  placeholder="What felt rare, well-structured, safe, or satisfying?"
                  maxLength={700}
                />
              </label>

              <label style={{ display: "block" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>
                  Where did you hesitate?
                </div>
                <textarea
                  className="ns-input"
                  style={{ minHeight: 70, resize: "vertical" }}
                  value={betaHesitate}
                  onChange={(e) => setBetaHesitate(e.target.value)}
                  placeholder="Refresh delays, unclear buttons, too many steps, anything that felt uncertain."
                  maxLength={700}
                />
              </label>

              <label style={{ display: "block" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>
                  Additional observations
                </div>
                <textarea
                  className="ns-input"
                  style={{ minHeight: 70, resize: "vertical" }}
                  value={betaMore}
                  onChange={(e) => setBetaMore(e.target.value)}
                  placeholder="Any other notes, suggestions, or concerns."
                  maxLength={700}
                />
              </label>
            </div>

            <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" className="ns-button ns-button-outline" onClick={closeBetaFeedback} disabled={betaBusy}>
                Cancel
              </button>
              <button type="button" className="ns-button ns-button-primary" onClick={submitBetaFeedback} disabled={betaBusy}>
                {betaBusy ? "Submitting..." : "Submit Feedback"}
              </button>
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
              Submitted as: <b>{displayIdentity || "User"}</b>
            </div>
          </div>
        </div>
      ) : null}

      <div className="ns-working-row">
        {isLoggedIn ? (
          <span className="ns-working-pill">
            Working as: <b>{displayIdentity}</b> <span>({roleLabel})</span>
          </span>
        ) : (
          <span className="ns-working-pill">{booting ? "Restoring session..." : "Not logged in (guest)"}</span>
        )}
      </div>

      <main key={activePage} className="ns-main ns-route-entry">{renderMain()}</main>
      <FooterBar
        onGo={onNavigatePage}
        lang={appLang}
      />
    </div>
  );
}
